/**
 * Client-side preview of dashboard top-bar aggregates after an optimistic
 * override. Approximates the backend's TR monthly sum + trailing full-month
 * average + position payout/leverage for snappy UI. The server AnalysisRun
 * from the override response remains authoritative; mismatches are logged
 * via logTopBarMismatch.
 */

import {
  withdrawalInLeverage,
  withdrawalMonthlyPayout,
} from "@/components/underwriting/funderLeverage";
import type {
  AnalysisDecision,
  AnalysisOverridePayload,
  AnalysisOverrideTarget,
  AnalysisRun,
  AnalysisRunTransaction,
} from "@/types/analysisRun";
import type { AnalysisOutput, FunderGroup, RevenueLine } from "@/types/underwriting";

const MONTH_ABBREVS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const MONEY_EPS = 1;
const LEVERAGE_EPS = 0.15;

export type TopBarSnapshot = {
  average_revenue: number;
  average_revenue_all_full_months: number;
  monthly_payout: number;
  current_leverage_pct: number;
  revenue_by_month: Record<string, number>;
};

export function fmtMoneyShort(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  // ≥ $1,000,000 → millions (e.g. $1.2M). ≥ $1,000 → thousands ($30k).
  if (abs >= 1_000_000) {
    const val = abs / 1_000_000;
    const rounded = Math.round(val * 10) / 10;
    return Number.isInteger(rounded) ? `${sign}$${rounded}M` : `${sign}$${rounded.toFixed(1)}M`;
  }
  if (abs >= 1000) {
    const val = abs / 1000;
    const rounded = Math.round(val * 10) / 10;
    return Number.isInteger(rounded) ? `${sign}$${rounded}k` : `${sign}$${rounded.toFixed(1)}k`;
  }
  return `${sign}$${Math.round(abs).toLocaleString("en-US")}`;
}

export function monthlyTrueRevenueByMonth(
  transactions: AnalysisRunTransaction[]
): Record<string, number> {
  const monthly: Record<string, number> = {};
  for (const tx of transactions) {
    const amount = Number(tx.amount);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    if (tx.rev !== "TR") continue;
    const month = (tx.date || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month)) continue;
    monthly[month] = (monthly[month] || 0) + amount;
  }
  return monthly;
}

function monthAbbrev(yyyyMm: string): string {
  const monthNum = Number(yyyyMm.slice(5, 7));
  return MONTH_ABBREVS[monthNum - 1] ?? yyyyMm;
}

function buildRevenueLines(months: string[], monthlyTr: Record<string, number>): RevenueLine[] {
  return months.map((m) => {
    const amount = monthlyTr[m] ?? 0;
    return {
      month_abbrev: monthAbbrev(m),
      amount,
      amount_display: fmtMoneyShort(amount),
    };
  });
}

function resolveMonths(
  rollups: Record<string, unknown> | undefined,
  monthlyTr: Record<string, number>,
  existing: RevenueLine[] | undefined
): string[] {
  const fromRollups = rollups?.months;
  if (Array.isArray(fromRollups) && fromRollups.every((m) => typeof m === "string")) {
    return fromRollups as string[];
  }
  const keys = Object.keys(monthlyTr).sort();
  if (keys.length > 0) return keys;
  // Fall back to preserving existing line count/order via reverse-mapping abbrevs is lossy;
  // keep empty rather than invent months.
  return existing?.length ? keys : [];
}

function resolveFullMonths(
  rollups: Record<string, unknown> | undefined,
  months: string[]
): string[] {
  const fromRollups = rollups?.full_months;
  if (Array.isArray(fromRollups) && fromRollups.every((m) => typeof m === "string")) {
    return fromRollups as string[];
  }
  return months;
}

function trailingAverage(monthlyTr: Record<string, number>, fullMonths: string[], window = 3) {
  const trailing = fullMonths.slice(-window);
  if (trailing.length === 0) return 0;
  const sum = trailing.reduce((acc, m) => acc + (monthlyTr[m] ?? 0), 0);
  return sum / trailing.length;
}

function payoutFromFunderGroups(groups: FunderGroup[] | undefined, businessDaysPerMonth = 20) {
  if (!groups?.length) return 0;
  let total = 0;
  for (const fg of groups) {
    for (const w of fg.withdrawals ?? []) {
      if (!withdrawalInLeverage(w)) continue;
      total += withdrawalMonthlyPayout(w, undefined, businessDaysPerMonth);
    }
  }
  return total;
}

export function snapshotTopBar(run: AnalysisRun): TopBarSnapshot | null {
  const output = run.analysis_output_json;
  if (!output || typeof output !== "object" || !("average_revenue" in output)) return null;
  const analysis = output as AnalysisOutput;
  const revenue_by_month: Record<string, number> = {};
  const rollups = (run.analysis_json?.rollups ?? {}) as Record<string, unknown>;
  const months = resolveMonths(
    rollups,
    monthlyTrueRevenueByMonth(run.transactions_json ?? []),
    analysis.revenue_lines
  );
  for (let i = 0; i < months.length; i++) {
    const line = analysis.revenue_lines?.[i];
    if (line) revenue_by_month[months[i]] = line.amount;
  }
  return {
    average_revenue: analysis.average_revenue ?? 0,
    average_revenue_all_full_months: analysis.average_revenue_all_full_months ?? 0,
    monthly_payout: analysis.monthly_payout ?? 0,
    current_leverage_pct: analysis.current_leverage_pct ?? 0,
    revenue_by_month,
  };
}

export function logTopBarMismatch(args: {
  runId: number;
  override: { target_type: string; target_key: string; field?: string; value?: unknown };
  optimistic: TopBarSnapshot;
  official: TopBarSnapshot;
}): void {
  const { optimistic, official } = args;
  const deltas = {
    average_revenue: official.average_revenue - optimistic.average_revenue,
    average_revenue_all_full_months:
      official.average_revenue_all_full_months - optimistic.average_revenue_all_full_months,
    monthly_payout: official.monthly_payout - optimistic.monthly_payout,
    current_leverage_pct: official.current_leverage_pct - optimistic.current_leverage_pct,
  };
  const material =
    Math.abs(deltas.average_revenue) > MONEY_EPS ||
    Math.abs(deltas.average_revenue_all_full_months) > MONEY_EPS ||
    Math.abs(deltas.monthly_payout) > MONEY_EPS ||
    Math.abs(deltas.current_leverage_pct) > LEVERAGE_EPS;

  if (!material) return;

  // Intentional: surface optimistic vs server drift for investigation.
  // eslint-disable-next-line no-console -- mismatch diagnostics for offer-critical aggregates
  console.warn("[optimisticAnalysisRecompute] mismatch vs server", {
    source: "optimisticAnalysisRecompute",
    runId: args.runId,
    override: args.override,
    optimistic,
    official,
    deltas,
  });
}

/** Recompute top-bar aggregates on a run that already has row-level patches applied. */
export function recomputeTopBarAggregates(
  run: AnalysisRun,
  businessDaysPerMonth = 20
): AnalysisRun {
  const output = run.analysis_output_json;
  if (!output || typeof output !== "object" || !("business_name" in output)) {
    return run;
  }
  const analysis = { ...(output as AnalysisOutput) };
  const rollups = (run.analysis_json?.rollups ?? {}) as Record<string, unknown>;
  const monthlyTr = monthlyTrueRevenueByMonth(run.transactions_json ?? []);
  const months = resolveMonths(rollups, monthlyTr, analysis.revenue_lines);
  const fullMonths = resolveFullMonths(rollups, months);
  const averageRevenue = trailingAverage(monthlyTr, fullMonths, 3);
  const avgAllFull =
    fullMonths.length > 0
      ? fullMonths.reduce((acc, m) => acc + (monthlyTr[m] ?? 0), 0) / fullMonths.length
      : 0;
  const monthlyPayout = payoutFromFunderGroups(analysis.funder_groups, businessDaysPerMonth);
  const leveragePct = averageRevenue > 0 ? (monthlyPayout / averageRevenue) * 100 : 0;

  analysis.revenue_lines = buildRevenueLines(months, monthlyTr);
  analysis.average_revenue = Math.round(averageRevenue * 100) / 100;
  analysis.average_revenue_display = `${fmtMoneyShort(averageRevenue)}/mo avg.`;
  analysis.average_revenue_all_full_months = Math.round(avgAllFull * 100) / 100;
  analysis.average_revenue_all_full_months_display = `${fmtMoneyShort(avgAllFull)}/mo avg.`;
  analysis.monthly_payout = Math.round(monthlyPayout * 100) / 100;
  analysis.monthly_payout_display = `$${Math.round(monthlyPayout).toLocaleString("en-US")}`;
  analysis.current_leverage_pct = Math.round(leveragePct * 10) / 10;

  const next: AnalysisRun = {
    ...run,
    analysis_output_json: analysis,
  };

  const decision = run.decision_json;
  if (decision && typeof decision === "object" && "computed" in decision) {
    const d = { ...(decision as AnalysisDecision) };
    d.computed = {
      ...d.computed,
      average_revenue: analysis.average_revenue,
      monthly_payout: analysis.monthly_payout,
      current_leverage_pct: analysis.current_leverage_pct,
    };
    next.decision_json = d;
  }

  return next;
}

function patchTransactionField(
  tx: AnalysisRunTransaction,
  field: string,
  value: unknown
): AnalysisRunTransaction {
  if (field === "rev") {
    return {
      ...tx,
      rev: value as AnalysisRunTransaction["rev"],
      overridden: true,
      override_notes: { ...(tx.override_notes ?? {}), rev: "" },
    };
  }
  if (field === "txn_type" && typeof value === "string") {
    return {
      ...tx,
      txn_type: value,
      overridden: true,
      override_notes: { ...(tx.override_notes ?? {}), txn_type: "" },
    };
  }
  return tx;
}

function patchFunderGroups(
  groups: FunderGroup[] | undefined,
  targetKey: string,
  field: string,
  value: unknown
): FunderGroup[] | undefined {
  if (!groups) return groups;
  return groups.map((fg) => ({
    ...fg,
    withdrawals: fg.withdrawals.map((w) => {
      if (w.override_key !== targetKey) return w;
      if (field === "include_in_leverage" && typeof value === "boolean") {
        return { ...w, include_in_leverage: value };
      }
      if (field === "frequency" && typeof value === "string") {
        const monthly = withdrawalMonthlyPayout({ ...w, frequency: value }, value);
        return { ...w, frequency: value, monthly_payout: monthly };
      }
      return w;
    }),
  }));
}

export function applyOverrideOptimistically(
  run: AnalysisRun,
  variables:
    | { action: "save"; payload: AnalysisOverridePayload }
    | { action: "clear"; payload: AnalysisOverrideTarget }
): AnalysisRun {
  const next: AnalysisRun = {
    ...run,
    transactions_json: [...(run.transactions_json ?? [])],
    analysis_json: { ...(run.analysis_json ?? {}) },
    analysis_output_json:
      run.analysis_output_json && typeof run.analysis_output_json === "object"
        ? { ...(run.analysis_output_json as AnalysisOutput) }
        : run.analysis_output_json,
  };

  if (variables.action === "save") {
    const { target_type, target_key, field, value } = variables.payload;
    if (target_type === "transaction") {
      next.transactions_json = next.transactions_json.map((tx) =>
        tx.override_key === target_key ? patchTransactionField(tx, field, value) : tx
      );
    } else if (target_type === "position") {
      const output = next.analysis_output_json as AnalysisOutput;
      next.analysis_output_json = {
        ...output,
        funder_groups: patchFunderGroups(output.funder_groups, target_key, field, value),
      };
      const positions = [...(next.analysis_json.positions ?? [])];
      next.analysis_json = {
        ...next.analysis_json,
        positions: positions.map((p) => {
          // Detected positions may not carry override_key on the raw analysis_json;
          // funder_groups are the source of truth for dashboard leverage. Still try.
          if ((p as { override_key?: string }).override_key !== target_key) return p;
          if (field === "include_in_leverage" && typeof value === "boolean") {
            return { ...p, include_in_leverage: value };
          }
          if (field === "frequency" && typeof value === "string") {
            return { ...p, frequency: value as typeof p.frequency };
          }
          return p;
        }),
      };
    }
  }

  return recomputeTopBarAggregates(next);
}

export function patchBorrowerTransactionsForOverride(
  rows: Array<{
    override_key?: string | null;
    is_revenue?: boolean;
    category?: string;
    tags_display?: string;
  }>,
  variables:
    | { action: "save"; payload: AnalysisOverridePayload }
    | { action: "clear"; payload: AnalysisOverrideTarget }
) {
  if (variables.action !== "save") return rows;
  const { target_type, target_key, field, value } = variables.payload;
  if (target_type !== "transaction") return rows;
  return rows.map((row) => {
    if (row.override_key !== target_key) return row;
    if (field === "rev") {
      return { ...row, is_revenue: value === "TR" };
    }
    if (field === "txn_type" && typeof value === "string") {
      return { ...row, category: value, tags_display: value };
    }
    return row;
  });
}

export function overrideMutationKey(
  variables:
    | { action: "save"; payload: AnalysisOverridePayload }
    | { action: "clear"; payload: AnalysisOverrideTarget }
): string {
  if (variables.action === "save") {
    const p = variables.payload;
    return `${p.target_type}|${p.target_key}|${p.field}`;
  }
  const p = variables.payload;
  return `${p.target_type}|${p.target_key}|${p.field || "*"}`;
}
